import React, { useState, useEffect } from "react";
import api from "../services/api";
import { useNavigate } from "react-router-dom";
import NavBar from "../components/navbar/navbar";
import { useTranslation } from "react-i18next";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";

export default function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "",
    region_id: "",
    district_id: "",
    eglise_id: "",
    birth_date: "",
    phone: ""
  });

  const [regions, setRegions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [eglises, setEglises] = useState([]);
  const [usedRegions, setUsedRegions] = useState([]);
  const [usedDistricts, setUsedDistricts] = useState([]);
  const [usedEglises, setUsedEglises] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Charger les IDs déjà utilisés
  useEffect(() => {
    Promise.all([
      api.get("/locations/used/regions").catch(() => ({ data: [] })),
      api.get("/locations/used/districts").catch(() => ({ data: [] })),
      api.get("/locations/used/eglises").catch(() => ({ data: [] }))
    ]).then(([regRes, distRes, eglRes]) => {
      setUsedRegions(regRes.data || []);
      setUsedDistricts(distRes.data || []);
      setUsedEglises(eglRes.data || []);
    });
  }, []);

  // Réinitialiser les champs si la valeur sélectionnée est maintenant utilisée
  useEffect(() => {
    if (formData.role === "coordinateur_region" && formData.region_id && usedRegions.includes(Number(formData.region_id))) {
      setFormData(prev => ({ ...prev, region_id: "" }));
    }
    if (formData.role === "coordinateur_district" && formData.district_id && usedDistricts.includes(Number(formData.district_id))) {
      setFormData(prev => ({ ...prev, district_id: "", eglise_id: "" }));
    }
    if (formData.role === "eglise" && formData.eglise_id && usedEglises.includes(Number(formData.eglise_id))) {
      setFormData(prev => ({ ...prev, eglise_id: "" }));
    }
  }, [formData.role, formData.region_id, formData.district_id, formData.eglise_id, usedRegions, usedDistricts, usedEglises]);

  // Charger régions
  useEffect(() => {
    api.get("/locations/regions")
      .then(res => setRegions(res.data || []))
      .catch(err => console.error("Erreur régions :", err));
  }, []);

  // Charger districts quand region change
  useEffect(() => {
    if (formData.region_id) {
      api.get(`/locations/districts?region_id=${formData.region_id}`)
        .then(res => setDistricts(res.data || []))
        .catch(err => console.error("Erreur districts :", err));
    } else {
      setDistricts([]);
    }
  }, [formData.region_id]);

  // Charger églises quand district change
  useEffect(() => {
    if (formData.district_id) {
      api.get(`/locations/eglises?district_id=${formData.district_id}`)
        .then(res => setEglises(res.data || []))
        .catch(err => console.error("Erreur églises :", err));
    } else {
      setEglises([]);
    }
  }, [formData.district_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Si changement de rôle, réinitialiser les champs selon le nouveau rôle
    if (name === "role") {
      // Si on passe à coordinateur_region, réinitialiser district et église
      if (value === "coordinateur_region") {
        setFormData({ ...formData, role: value, district_id: "", eglise_id: "" });
      } else {
        // Pour coordinateur_district et eglise, on garde la région si elle existe
        setFormData({ ...formData, role: value });
      }
    } 
    // reset hiérarchie dépendante
    else if (name === "region_id") {
      setFormData({ ...formData, region_id: value, district_id: "", eglise_id: "" });
    } else if (name === "district_id") {
      setFormData({ ...formData, district_id: value, eglise_id: "" });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Validation selon le rôle
    if (!formData.region_id) {
      setError("Veuillez sélectionner une région.");
      return;
    }

    // Pour coordinateur_district et eglise, district et église sont requis
    if (formData.role === "coordinateur_district" || formData.role === "eglise") {
      if (!formData.district_id || !formData.eglise_id) {
        setError("Veuillez remplir tous les champs (région, district et église) avant de soumettre l'inscription.");
        return;
      }
    }

    const cleanData = {
      ...formData,
      region_id: formData.region_id || null,
      district_id: formData.district_id || null,
      eglise_id: formData.eglise_id || null,
    };

    try {
      await api.post("/auth/register", cleanData);
      setSuccess("Inscription effectuée avec succès ! Votre compte est en attente de validation par l'administrateur. Vous recevrez votre login par email une fois votre compte validé.");
      setTimeout(() => navigate("/login"), 5000);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.error || t("register_error"));
    }
  };

  return (
    <div>
      <NavBar/>
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-body-secondary">
        <style>{`
          .register-card {
            max-width: 500px;
            border-radius: 15px;
          }
        `}</style>

        <div className="card register-card shadow p-4 border-0 bg-white w-100">
          <h3 className="text-center text-primary mb-3">{t("register_title")}</h3>

          {error && <div className="alert alert-danger">{error}</div>}
          {success && (
            <div className="alert alert-success">
              <i className="bi bi-check-circle me-2"></i>
              <div>
                <strong>Inscription effectuée avec succès !</strong>
                <br />
                Votre compte est en attente de validation par l'administrateur. Vous recevrez votre login par email une fois votre compte validé.
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="mb-3">
              <label htmlFor="name" className="form-label">{t("name_label")}</label>
              <input
                id="name"
                type="text"
                name="name"
                className="form-control"
                value={formData.name}
                onChange={handleChange}
                required
                autoComplete="name"
              />
            </div>

            <div className="mb-3">
              <label htmlFor="email" className="form-label">{t("email_label")}</label>
              <input
                id="email"
                type="email"
                name="email"
                className="form-control"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="email"
              />
            </div>

            <div className="mb-3">
              <label htmlFor="password" className="form-label">{t("password_label")}</label>
              <input
                id="password"
                type="password"
                name="password"
                className="form-control"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="new-password"
              />
            </div>

            <div className="mb-3">
              <label htmlFor="birth_date" className="form-label">{t("birth_date_label")}</label>
              <input
                id="birth_date"
                type="date"
                name="birth_date"
                className="form-control"
                value={formData.birth_date}
                onChange={handleChange}
                required
              />
            </div>

            <div className="mb-3">
              <label htmlFor="phone" className="form-label">{t("phone_label")}</label>
              <input
                id="phone"
                type="tel"
                name="phone"
                className="form-control"
                value={formData.phone}
                onChange={handleChange}
                required
                autoComplete="tel"
              />
            </div>

            <div className="mb-3">
              <label htmlFor="role" className="form-label">{t("role_label")}</label>
              <select
                id="role"
                name="role"
                className="form-select"
                value={formData.role}
                onChange={handleChange}
                required
              >
                <option value="">{t("select_role_placeholder")}</option>
                <option value="coordinateur_district">{t("role_coord_district")}</option>
                <option value="coordinateur_region">{t("role_coord_region")}</option>
                <option value="eglise">{t("role_eglise")}</option>
              </select>
            </div>

            {/* coordinateur_région : Région requise uniquement */}
            {formData.role === "coordinateur_region" && (
              <div className="mb-3">
                <label htmlFor="region_id" className="form-label">{t("region_label")}</label>
                <select
                  id="region_id"
                  name="region_id"
                  className="form-select"
                  value={formData.region_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">{t("select_region_placeholder")}</option>
                  {regions
                    .filter(r => !usedRegions.includes(r.id))
                    .map((r) => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                </select>
              </div>
            )}

            {/* coordinateur_district : Région + District + Église requis */}
            {formData.role === "coordinateur_district" && (
              <>
                <div className="mb-3">
                  <label htmlFor="region_id_cd" className="form-label">{t("region_label")}</label>
                  <select
                    id="region_id_cd"
                    name="region_id"
                    className="form-select"
                    value={formData.region_id}
                    onChange={handleChange}
                    required
                  >
                    <option value="">{t("select_region_placeholder")}</option>
                    {regions.map((r) => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label htmlFor="district_id_cd" className="form-label">{t("district_label")}</label>
                  <select
                    id="district_id_cd"
                    name="district_id"
                    className="form-select"
                    value={formData.district_id}
                    onChange={handleChange}
                    required
                    disabled={!formData.region_id}
                  >
                    <option value="">{t("select_district_placeholder")}</option>
                    {districts
                      .filter(d => !usedDistricts.includes(d.id))
                      .map((d) => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label htmlFor="eglise_id_cd" className="form-label">{t("eglise_label")}</label>
                  <select
                    id="eglise_id_cd"
                    name="eglise_id"
                    className="form-select"
                    value={formData.eglise_id}
                    onChange={handleChange}
                    required
                    disabled={!formData.district_id}
                  >
                    <option value="">{t("select_eglise_placeholder")}</option>
                    {eglises.map((e) => (
                      <option key={e.id} value={e.id}>{e.name}</option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {/* église : Région + District + Église requis */}
            {formData.role === "eglise" && (
              <>
                <div className="mb-3">
                  <label htmlFor="region_id_e" className="form-label">{t("region_label")}</label>
                  <select
                    id="region_id_e"
                    name="region_id"
                    className="form-select"
                    value={formData.region_id}
                    onChange={handleChange}
                    required
                  >
                    <option value="">{t("select_region_placeholder")}</option>
                    {regions.map((r) => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label htmlFor="district_id_e" className="form-label">{t("district_label")}</label>
                  <select
                    id="district_id_e"
                    name="district_id"
                    className="form-select"
                    value={formData.district_id}
                    onChange={handleChange}
                    required
                    disabled={!formData.region_id}
                  >
                    <option value="">{t("select_district_placeholder")}</option>
                    {districts.map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label htmlFor="eglise_id_e" className="form-label">{t("eglise_label")}</label>
                  <select
                    id="eglise_id_e"
                    name="eglise_id"
                    className="form-select"
                    value={formData.eglise_id}
                    onChange={handleChange}
                    required
                    disabled={!formData.district_id}
                  >
                    <option value="">{t("select_eglise_placeholder")}</option>
                    {eglises
                      .filter(e => !usedEglises.includes(e.id))
                      .map((e) => (
                        <option key={e.id} value={e.id}>{e.name}</option>
                      ))}
                  </select>
                </div>
              </>
            )}

            <button type="submit" className="btn btn-primary w-100">
              <i className="bi bi-person-plus me-2" />
              {t("submit_register")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
